import {
    IHttpResponse,
    IHttpService,
    IPromise,
    IQService,
    IRequestShortcutConfig,
} from 'angular';
import {
    hasOwnProperty,
    setAuthorizationHeader,
    standardizeUrl,
} from '../../tn.common';
import { Subject } from 'rxjs';

/**
 * These are models that we have created to use in our app the represent django types
 * it is useful for Generic type constraints.
 */
export type DjangoModels = unknown;

/**
 * All GET requests to our Django API return an IPagedResult
 *
 * TODO: rename to whatever django calls it.
 */
export interface IPagedResult<T = unknown> {
    // total number of records for query
    count: number;
    // link to the next page of query results
    next: string | null;
    // link to the previous page of query results.
    previous: string | null;
    // the records returned from django
    results: T[];
}

/**
 * Type Guard for isIPagedResult
 */
export function isIPagedResult<T = unknown>(
    obj: unknown
): obj is IPagedResult<T> {
    return (
        typeof obj === 'object' &&
        hasOwnProperty(obj, 'results') &&
        hasOwnProperty(obj, 'next') &&
        hasOwnProperty(obj, 'count')
    );
}

/**
 * Unwrap IHttpResponse.data
 *
 * We want to encapsulate the HTTP details in our class to make it easy for consumers to use.
 * Let's try not toe expose the IHttpResponse, unless we find a really compelling use case.
 */
function unwrapIHttpResponse<T = unknown>(result: IHttpResponse<T>) {
    return result.data;
}

/**
 * The DjangoClient is global is meant to be composed into services. The class itself is
 * not Generic, but it's method are. Typed consumers should use it as such.
 */
export default class DjangoClient {
    static $inject = ['$q', '$http', 'apiBaseURl'];

    // event emitter for when the token is invalid. provides request config to listeners can retry the request.
    public invalidToken: Subject<IRequestShortcutConfig> = new Subject();
    // need to set the access token.
    public accessToken = '';

    constructor(
        protected $q: IQService,
        protected $http: IHttpService,
        protected baseUrl: string
    ) {}

    /**
     * Factory to create a catch handler that will notify the UI that the user is not authenticated when
     * their token is invalid by calling userService.logout(), then will attempt to re-run the query as an
     * anonymous user.
     *
     * TODO: we should re-think how we're handling an invalid token... We should probably check to see if there is a valid
     * session still. I feel like there should be userService.silentRenew() that attempts to renew the token, and fires logout
     * if unsuccessful.  There was also an AuthInterception that was supposed to send out an event for something like this...
     * regardless we should think about a better approach to retries.
     *
     * @param request - a function that will make the necessary request and return the results.
     */
    protected _logoutAndRerun<T = unknown>(
        request: () => IPromise<T>,
        config: IRequestShortcutConfig
    ): (err: { data: { detail: string } }) => IPromise<T> {
        const logoutCatch = async (err: {
            data: { detail: string };
        }): Promise<T> => {
            // if our token is invalid, emit an event for consumers to handle. UserService may try to renew the token.
            if (err?.data?.detail === 'Invalid token.') {
                this.invalidToken.next(config);
                const request_token = config?.headers?.Authorization;
                // if the token is not the same as the initial request, assume the token was updated and retry the request with the new token.
                if (this.accessToken !== request_token) {
                    this._setAuthorizationHeader(config);
                } else {
                    // otherwise clear the local token
                    this.accessToken = '';
                    // and retry the request as an anonymous user.
                    if (config?.headers?.Authorization) {
                        delete config.headers.Authorization;
                    }
                }
                return request();
            }
            //otherwise rethrow the error
            throw err;
        };

        return logoutCatch;
    }

    /**
     * Set the Authorizaion header to the current users token if it is not already in the config.
     * @param config - the config to add the header to
     */
    protected _setAuthorizationHeader(
        config: IRequestShortcutConfig = {}
    ): IRequestShortcutConfig {
        return setAuthorizationHeader(this.accessToken, config);
    }

    /**
     * Run a get request againt the Typenetwork Django API
     *
     * url querystring params that should work on all django endpoints, we probably need to refer to an api doc instead of putting it all here.
     * - page_size - number of records to include on a page
     * - page - page relative to the page_size to load.
     *
     * TODO: document all the universal querystring params accepted by all django endpoints.
     *
     * @template T - T must be one of DjangoModels or unknown
     * @param url - path or absolute url with or without querystring
     * @param config - override the default config.
     */
    public get<T = unknown>(
        url: string,
        config: IRequestShortcutConfig = {}
    ): IPromise<T> {
        const _config = this._setAuthorizationHeader(config);
        // set a default ttl of 10 seconds to help dedup requests on page loads.
        _config.ttl = 10000;
        const _url = this.standardizeUrl(url);
        const request = this.$http.get<T>(_url, _config);
        const response = request
            .then(unwrapIHttpResponse)
            .catch(
                this._logoutAndRerun<T>(() => this.get<T>(url, config), config)
            );
        return response;
    }

    /**
     * Typed wrapped for paginated results so you can pass IDjangoModel instead of IPagedResult<IDjangoModel> to the template.
     *
     * @template T - T must be one of DjangoModels or unknown
     * @param url - path or absolute url with or without querystring
     * @param config - override the default config.
     */
    public getPaged<T = unknown>(
        url: string,
        config: IRequestShortcutConfig = {}
    ): IPromise<IPagedResult<T>> {
        return this.get<IPagedResult<T>>(url, config);
    }

    /**
     * Get all records from a paginated set, use this with caution because it will fire the requests in rapid
     * succession blocking other requests. You should only use this when only a handful of pages will be queried.
     *
     * @deprecated consumers should be re-written to grab data as needed or in a single query.
     *
     * @param url - path or absolute url with or without querystring
     * @param config - override the default config.
     * @returns
     */
    public getAllPages<T = unknown>(
        url: string,
        config: IRequestShortcutConfig = {}
    ): IPromise<IPagedResult<T>> {
        const _config = this._setAuthorizationHeader(config);
        return this._getNextPage<T>(url, _config, []).catch(
            this._logoutAndRerun<IPagedResult<T>>(
                () => this.getAllPages<T>(url, config),
                config
            )
        );
    }

    /**
     * recursive method used by getAllPages to aggregate a full collection of pages.
     */
    protected _getNextPage<T = unknown>(
        url: string,
        config: IRequestShortcutConfig,
        accumulation: T[]
    ): IPromise<IPagedResult<T>> {
        const _url = this.standardizeUrl(url);

        const request = this.$http.get<IPagedResult<T>>(_url, config);
        const response = request.then((result) => {
            const pagedResult = result.data;
            // merge the data from previous call, think of this like a recursive reducer.
            accumulation = accumulation.concat(result.data.results);

            if (pagedResult.next) {
                // if there are more pages, keep calling them
                return this._getNextPage<T>(
                    pagedResult.next,
                    config,
                    accumulation
                );
            } else {
                // after all the pages are called return the accumulation
                const aggregatePagedResult: IPagedResult<T> = {
                    next: null,
                    previous: null,
                    count: result.data.count,
                    results: accumulation,
                };
                // keep it wrapped in a promise for response consistency.
                return this.$q.resolve(aggregatePagedResult);
            }
        });
        return response;
    }

    /**
     * Get next page using the result from a previous query.
     **/
    public getNextPage<T = unknown>(
        result: IPagedResult<T>,
        config = {}
    ): IPromise<void> | IPromise<IPagedResult<T>> {
        if (result.next) {
            return this.getPaged<T>(result.next, config);
        }
        return this.$q.resolve();
    }

    /**
     * Send a POST to Django, typically creates an object.
     *
     * TODO: Identify if we need any additional type constraints here to help hint Django's api semantics.
     * @param url - path or absolute url with or without querystring, typically addresses a list endpoint
     * @param data  - object to create
     * @param config - override the default config.
     */
    public post<T = unknown, D = unknown>(
        url: string,
        data?: D,
        config: IRequestShortcutConfig = {}
    ): IPromise<T> {
        config = this._setAuthorizationHeader(config);
        url = this.standardizeUrl(url);
        return this.$http.post<T>(url, data, config).then(unwrapIHttpResponse);
    }

    /**
     * Send a PUT to Django, typically updates an object.
     *
     * TODO: Identify if we need any additional type constraints here to help hint Django's api semantics.
     * @param url - path or absolute url with or without querystring, typically addresses a single entity
     * @param data  - data to update object with
     * @param config - override the default config.
     */
    public put<T = unknown, D = unknown>(
        url: string,
        data?: D,
        config: IRequestShortcutConfig = {}
    ): IPromise<T> {
        config = this._setAuthorizationHeader(config);
        url = this.standardizeUrl(url);
        return this.$http.put<T>(url, data, config).then(unwrapIHttpResponse);
    }

    /**
     * Send a DELETE to Django, typically deletes an object.
     *
     * TODO: Identify if we need any additional type constraints here to help hint Django's api semantics.
     * @param url - path or absolute url with or without querystring, typically addresses a single entity.
     * @param config - override the default config.
     */
    public del<T = unknown>(
        url: string,
        config: IRequestShortcutConfig = {}
    ): IPromise<T> {
        config = this._setAuthorizationHeader(config);
        url = this.standardizeUrl(url);
        return this.$http.delete<T>(url, config).then(unwrapIHttpResponse);
    }

    /**
     * Ensure a url is absolute, prepends baseUrl to all paths.
     * @param pathOrAbsolute
     * @returns
     */
    protected standardizeUrl(pathOrAbsolute: string): string {
        return standardizeUrl(this.baseUrl, pathOrAbsolute);
    }
}
